import React from 'react';
import {
  Box,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Restaurant as RestaurantIcon,
  Timer as TimerIcon,
  AutoAwesome as AutoAwesomeIcon,
  Blender as BlenderIcon
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  border: 'none',
  marginBottom: theme.spacing(3),
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
}));

const skillLabels = [
  { value: 1, label: 'Principiante' },
  { value: 2, label: 'Básico' },
  { value: 3, label: 'Intermedio' },
  { value: 4, label: 'Avanzado' },
  { value: 5, label: 'Chef' },
];

const StyledSlider = styled(Slider)(({ theme }) => ({
  color: '#667eea',
  height: 8,
  '& .MuiSlider-track': {
    border: 'none',
    borderRadius: 4,
  },
  '& .MuiSlider-rail': {
    backgroundColor: '#e0e7ff',
    borderRadius: 4,
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '3px solid currentColor',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 0 8px rgba(102, 126, 234, 0.16)',
    },
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: '#667eea',
    borderRadius: '8px',
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#c7d2fe',
    height: 8,
    width: 8,
    borderRadius: '50%',
  },
  '& .MuiSlider-markActive': {
    backgroundColor: '#667eea',
  },
}));

const CulinaryExperience = ({ userData, handleInputChange }) => {
  const kitchenEquipment = [
    { name: 'Horno', icon: '🔥' },
    { name: 'Batidora', icon: '🍶' },
    { name: 'Procesador de alimentos', icon: '🔪' },
    { name: 'Olla a presión', icon: '🫕' },
    { name: 'Freidora de aire', icon: '🌀' }
  ];

  const handleEquipmentChange = (equipment) => {
    const newEquipment = userData.kitchenEquipment.includes(equipment)
      ? userData.kitchenEquipment.filter(e => e !== equipment)
      : [...userData.kitchenEquipment, equipment];
    handleInputChange('kitchenEquipment', newEquipment);
  };

  const getSkillLabel = (value) => {
    const skill = skillLabels.find(s => s.value === value);
    return skill ? skill.label : '';
  };

  return (
    <Box>
      {/* Cooking Skill Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#fef3c7' }}>
              <RestaurantIcon sx={{ color: '#f59e0b' }} />
            </IconBox>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Nivel de habilidad en la cocina
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ¿Cuánta experiencia tienes cocinando?
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <StyledSlider
              value={userData.cookingSkill || 1}
              onChange={(e, newValue) => handleInputChange('cookingSkill', newValue)}
              step={1}
              marks={skillLabels}
              min={1}
              max={5}
              valueLabelDisplay="auto"
              valueLabelFormat={getSkillLabel}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: '#667eea',
                bgcolor: '#e0e7ff',
                px: 3,
                py: 1,
                borderRadius: '12px'
              }}
            >
              {getSkillLabel(userData.cookingSkill || 1)}
            </Typography>
          </Box>
        </CardContent>
      </StyledCard>

      {/* Cooking Time Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#dbeafe' }}>
              <TimerIcon sx={{ color: '#3b82f6' }} />
            </IconBox>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Tiempo disponible para cocinar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ¿Cuánto tiempo puedes dedicar a cada comida?
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {[
              { value: '15 min', label: '15 min', emoji: '⚡' },
              { value: '30 min', label: '30 min', emoji: '🕐' },
              { value: '1 hora', label: '1 hora', emoji: '👨‍🍳' },
              { value: 'más de 1 hora', label: '+1 hora', emoji: '🍽️' },
            ].map((option) => (
              <Grid item xs={6} key={option.value}>
                <Box
                  onClick={() => handleInputChange('cookingTime', option.value)}
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    border: '2px solid',
                    borderColor: userData.cookingTime === option.value ? '#667eea' : '#e2e8f0',
                    bgcolor: userData.cookingTime === option.value ? '#e0e7ff' : '#fff',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#667eea',
                      transform: 'scale(1.02)',
                    }
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 0.5 }}>{option.emoji}</Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={userData.cookingTime === option.value ? 700 : 500}
                    color={userData.cookingTime === option.value ? '#667eea' : '#64748b'}
                  >
                    {option.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </StyledCard>

      {/* New Recipes Interest Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconBox sx={{ bgcolor: '#dcfce7' }}>
                <AutoAwesomeIcon sx={{ color: '#22c55e' }} />
              </IconBox>
              <Box>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  Explorar nuevas recetas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ¿Te gustaría descubrir recetas nuevas?
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={userData.newRecipesInterest || false}
              onChange={(e) => handleInputChange('newRecipesInterest', e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#667eea',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#667eea',
                  },
                },
              }}
            />
          </Box>
        </CardContent>
      </StyledCard>

      {/* Kitchen Equipment Card - Hidden for now */}
      {/* Equipment section removed as per user request */}
    </Box>
  );
};

export default CulinaryExperience;